import React from 'react';
import { CheckCircle, Circle, Clock } from 'lucide-react';

interface BookingProgressProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
}

interface Step {
  number: number;
  title: string;
  description: string;
}

export function BookingProgress({ currentStep, totalSteps = 4, className = "" }: BookingProgressProps) {
  const steps: Step[] = [
    { number: 1, title: "Select Dates", description: "Choose your adventure dates" },
    { number: 2, title: "Customize", description: "Select your preferences" },
    { number: 3, title: "Your Details", description: "Contact information" },
    { number: 4, title: "Submit", description: "Complete your booking request" }
  ];

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepNumber: number) => {
    const status = getStepStatus(stepNumber);
    
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'current':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getProgressPercentage = () => {
    return Math.max(0, Math.min(100, ((currentStep - 1) / (totalSteps - 1)) * 100));
  };

  return (
    <div className={`bg-gradient-to-r from-emerald-50 to-purple-50 rounded-xl p-4 lg:p-6 border border-blue-100 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Booking Progress</h3>
          <span className="text-sm text-gray-600 mt-1 block">
            Follow the steps below to customize and request your adventure booking
          </span>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">
            {currentStep}/{totalSteps}
          </div>
          <div className="text-xs text-gray-500">Steps</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-gray-500">Progress</span>
          <span className="text-xs font-medium text-gray-700">
            {Math.round(getProgressPercentage())}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-emerald-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, index) => {
          const status = getStepStatus(step.number);
          const isLast = index === steps.length - 1;
          
          return (
            <div key={step.number} className="relative">
              <div className="flex items-center gap-3">
                {/* Step Icon */}
                <div className={`
                  flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300
                  ${status === 'completed' ? 'bg-green-50 border-green-200' : 
                    status === 'current' ? 'bg-blue-50 border-blue-200' : 
                    'bg-gray-50 border-gray-200'}
                `}>
                  {getStepIcon(step.number)}
                </div>

                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className={`
                      text-sm font-medium transition-colors duration-300
                      ${status === 'completed' ? 'text-green-700' :
                        status === 'current' ? 'text-blue-700' :
                        'text-gray-500'}
                    `}>
                      {step.title}
                    </h4>
                    {status === 'current' && (
                      <div className="flex space-x-1">
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    )}
                  </div>
                  <p className={`
                    text-xs transition-colors duration-300
                    ${status === 'completed' ? 'text-green-600' :
                      status === 'current' ? 'text-blue-600' :
                      'text-gray-400'}
                  `}>
                    {step.description}
                  </p>
                </div>

                {/* Step Number Badge */}
                <div className={`
                  flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
                  ${status === 'completed' ? 'bg-green-100 text-green-700' :
                    status === 'current' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-500'}
                `}>
                  {step.number}
                </div>
              </div>

              {/* Connecting Line */}
              {!isLast && (
                <div className="absolute left-4 top-8 w-px h-6 bg-gray-200"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Tip */}
      {currentStep < totalSteps && (
        <div className="mt-4 p-3 bg-white/60 rounded-lg border border-blue-100">
          <div className="text-xs text-gray-600 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
            Complete step {currentStep} to continue to the next section
          </div>
        </div>
      )}
    </div>
  );
}
